import * as Api from '@ViewModels';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_QUERY_KEY = 'boardStageItems';

const getQueryFn =
	({ stageId, userSession, expand }: { stageId: string; userSession: Api.UserSessionContext; expand?: string }) =>
	async () => {
		return userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.IOpportunity>>(
			Api.ImpersonationBroker.composeApiUrl({
				urlPath: `board/stage/${stageId}/items`,
				queryParams: expand ? { expand } : undefined,
			}),
			'GET'
		);
	};

export function useBoardStageItems({
	enabled = true,
	stageId,
	refetchInterval,
	expand,
}: {
	enabled?: boolean;
	stageId: string;
	refetchInterval?: number;
	expand?: string;
}) {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryFn: getQueryFn({ stageId, userSession, expand }),
		queryKey: [BASE_QUERY_KEY, stageId, expand],
		refetchOnWindowFocus: false,
		refetchInterval,
	});
}

export const invalidateBoardStageItems = (stageId: string) => {
	reactQueryClient.invalidateQueries({
		queryKey: [BASE_QUERY_KEY, stageId],
	});
};

export const invalidateAllBoardStageItems = () => {
	reactQueryClient.invalidateQueries({
		queryKey: [BASE_QUERY_KEY],
	});
};

export const useBoardStageItemsMultiple = (
	stageIds: string[],
	enabled: boolean,
	refetchInterval?: number,
	expand?: string
) => {
	const userSession = useUserSession();
	return useQueries({
		queries: stageIds.map(stageId => {
			return {
				enabled,
				queryFn: getQueryFn({ stageId, userSession, expand }),
				queryKey: [BASE_QUERY_KEY, stageId, expand],
				refetchInterval,
			};
		}),
	});
};
